import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { AppProvider, useAppData } from '@/context/AppContext';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import OnboardingPage from '@/pages/OnboardingPage';
import DashboardPage from '@/pages/DashboardPage';
import ExercisePage from '@/pages/ExercisePage';
import NutritionPage from '@/pages/NutritionPage';
import ProgressPage from '@/pages/ProgressPage';
import SettingsPage from '@/pages/SettingsPage';
import TermsPage from '@/pages/TermsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import DisclaimerPage from '@/pages/DisclaimerPage';
import SupportPage from '@/pages/SupportPage';
import NotesPage from '@/pages/NotesPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import styles from './App.module.css';

function BerylLoader({ message }: { message: string }) {
  return (
    <div className={styles.loaderRoot}>
      <div className={styles.loaderContent}>
        <span className={styles.loaderBrand}>beryl</span>
        <span className={styles.loaderTagline}>Your Recovery Buddy</span>
        <div className={styles.spinner} />
        <div className={styles.loaderDots}>
          <div className={`${styles.dot} ${styles.dot1}`} />
          <div className={`${styles.dot} ${styles.dot2}`} />
          <div className={`${styles.dot} ${styles.dot3}`} />
        </div>
        <span className={styles.loaderMessage}>{message}</span>
      </div>
    </div>
  );
}

function AppContent() {
  const { onboardingComplete, loading, setSetting } = useAppData();

  if (loading) {
    return <BerylLoader message="Loading your recovery data..." />;
  }

  if (!onboardingComplete) {
    return (
      <OnboardingPage
        onComplete={async (data) => {
          if (data.userName) await setSetting('userName', data.userName);
          if (data.profilePhoto) await setSetting('profilePhoto', data.profilePhoto);
          await setSetting('recoveryStartDate', data.recoveryStartDate);
          await setSetting('injuryType', data.injuryType);
          await setSetting('fractureLocation', data.fractureLocation);
          await setSetting('injurySport', data.injurySport);
          await setSetting('manualRecoveryStage', String(data.initialStage));
          await setSetting('stageHistory', JSON.stringify([{ stage: data.initialStage, date: new Date().toISOString() }]));
          await setSetting('onboardingComplete', 'true');
        }}
      />
    );
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/exercises" element={<ExercisePage />} />
        <Route path="/nutrition" element={<NutritionPage />} />
        <Route path="/progress" element={<ProgressPage />} />
        <Route path="/notes" element={<NotesPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
    </Routes>
  );
}

function AppRoutes() {
  const { session, user, loading } = useAuth();

  if (loading) {
    return <BerylLoader message="Checking your account..." />;
  }

  if (!session) {
    return <LoginPage />;
  }

  return (
    <AppProvider userId={user!.id}>
      <AppContent />
    </AppProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/disclaimer" element={<DisclaimerPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/*" element={
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}
